// Invoice detail (BUILD_SPEC.md §8.4 step 5). Plan.md G23. DRAFT -> Approve
// (renders the PDF, marks Xero pending); APPROVED -> Void guard (SYNCED/PAID
// invoices cannot be voided — services/api's `transitionInvoice` enforces this,
// the button is simply not offered once SYNCED/PAID) + "Create credit note".
import { Alert, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertTriangle, IconCheck, IconFileInvoice } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { CustomerName } from './components/EntityNames';
import { DownloadPdfLink } from './components/DownloadPdfLink';
import { formatDate, formatMoneyCents, formatQty } from './format';
import { useApproveInvoice, useInvoice, useVoidInvoice } from './api';

const CREDITABLE_STATUSES = new Set(['APPROVED', 'SYNCED', 'PAID']);

export function InvoiceDetail({ invoiceId }: { invoiceId: string }) {
  const auth = useAuth();
  const canInvoice = auth.activeTenant?.role === 'SALES_ACCOUNTS' || auth.activeTenant?.role === 'ADMIN';
  const { data: invoice, isLoading, isError } = useInvoice(invoiceId);
  const approve = useApproveInvoice(invoiceId);
  const voidInvoice = useVoidInvoice(invoiceId);

  if (isLoading) return <Loader />;
  if (isError || !invoice) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Invoice not found.
      </Alert>
    );
  }

  const actionError = approve.error ?? voidInvoice.error;

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{invoice.number}</Title>
          <DocStatusBadge status={invoice.status} domain="invoice" />
        </Group>
        <Button component={Link} to="/sales-invoicing/invoices" variant="subtle">
          Back to register
        </Button>
      </Group>

      {(approve.isError || voidInvoice.isError) && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {actionError instanceof ApiRequestError ? actionError.message : 'Something went wrong.'}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Customer:</Text>
            <CustomerName id={invoice.customerId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Issue date:</Text>
            <Text>{formatDate(invoice.issueDate)}</Text>
          </Group>
          <Group gap={6}>
            <Text fw={600}>Due date:</Text>
            <Text>{formatDate(invoice.dueDate)}</Text>
          </Group>
          {invoice.periodFrom && (
            <Group gap={6}>
              <Text fw={600}>Period:</Text>
              <Text>
                {formatDate(invoice.periodFrom)} – {formatDate(invoice.periodTo)}
              </Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Description</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th>VAT</Table.Th>
                <Table.Th ta="right">Unit price</Table.Th>
                <Table.Th ta="right">Line total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoice.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>{line.description}</Table.Td>
                  <Table.Td>{formatQty(line.qty)}</Table.Td>
                  <Table.Td>{line.vatTreatment}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.unitPriceCents)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.lineTotalCents)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Divider my="sm" />
        <Stack gap={2} align="flex-end">
          <Text size="sm">Subtotal: {formatMoneyCents(invoice.subtotalCents)}</Text>
          <Text size="sm">VAT: {formatMoneyCents(invoice.vatCents)}</Text>
          <Text fw={700} size="lg">
            Total: {formatMoneyCents(invoice.totalCents)}
          </Text>
        </Stack>
      </Paper>

      <Group>
        <DownloadPdfLink label="Download invoice PDF" pdfKey={invoice.pdfKey} downloadPath={`/invoicing/invoices/${invoice.id}/pdf`} />
        {CREDITABLE_STATUSES.has(invoice.status) && canInvoice && (
          // A bare `Link` (not Mantine's `component={Link}` polymorphism) so
          // TanStack Router's `search` param type is inferred correctly against
          // the target route — mirrors features/search/SearchResults.tsx's
          // established pattern for this exact limitation.
          <Link to="/sales-invoicing/credit-notes/new" search={{ invoiceId: invoice.id }}>
            <Button component="span" variant="light" leftSection={<IconFileInvoice size={16} />}>
              Create credit note
            </Button>
          </Link>
        )}
      </Group>

      {canInvoice && (
        <Group justify="flex-end">
          {invoice.status === 'DRAFT' && (
            <Tooltip label="Approves this invoice — locks it, issues the PDF, and queues it for Xero sync.">
              <Button leftSection={<IconCheck size={16} />} loading={approve.isPending} onClick={() => approve.mutate(undefined)}>
                Approve
              </Button>
            </Tooltip>
          )}
          {invoice.status === 'APPROVED' && (
            <ConfirmButton
              color="red"
              variant="light"
              loading={voidInvoice.isPending}
              label="Void"
              // Real consequence, verified against services/api's invoices.ts
              // `/void` handler: it only flips this invoice's own status —
              // the dispatch(es) it billed stay POSTED and stay linked to
              // these invoice lines, so voiding does NOT return them to the
              // uninvoiced queue. There is no automatic way to re-bill them
              // from here; raise a credit note instead if the customer needs
              // a correction.
              message={`Void invoice ${invoice.number}? It will be excluded from Xero sync. The dispatch(es) it billed stay posted and stay linked to this invoice — voiding does not return them to the uninvoiced queue, so there is no automatic way to re-bill them. Raise a credit note instead if a correction is needed.`}
              onConfirm={() => voidInvoice.mutate(undefined)}
            />
          )}
        </Group>
      )}
    </Stack>
  );
}
